import React, { useMemo, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, X } from "lucide-react";
import { TemplateModule } from "../../../../views/marketing-pages/templates";
import { ActionNode } from "../../../../views/convoauto-pages/bot-studio/types";
import { CardShell, FieldLabel } from "./card-handler";
import { DraftDrawer } from "../canva-drawer";
import { EmailMockUp } from "../../campaign/email-mockup";

// Channel picker options — the two channels the New Lead flow supports
// today. Extend by adding entries here + a filter case in filteredTemplates.
const SEND_CHANNELS: { value: "Email" | "WhatsApp"; label: string }[] = [
  { value: "Email",    label: "Email" },
  { value: "WhatsApp", label: "WhatsApp" },
];

interface SendEmailCardProps {
  node: ActionNode;
  templates?: TemplateModule[];
  onUpdate: (config: Record<string, any>) => void;
  onDelete: () => void;
  onReset: () => void;
  onCloseConvoAI?: () => void;
  bare?: boolean;
}

// ─── Preview Panel — portaled to body ────────────────────────────────────────

interface PreviewPanelProps {
  isOpen: boolean;
  onClose: () => void;
  subject: string;
  previewText: string;
  senderName: string;
  senderEmail: string;
  htmlContent: string;
  plainText: string;
}

const PreviewPanel: React.FC<PreviewPanelProps> = ({
  isOpen,
  onClose,
  subject,
  previewText,
  senderName,
  senderEmail,
  htmlContent,
  plainText,
}) => {
  // Don't mount anything until first open
  const [everOpened, setEverOpened] = useState(false);
  useEffect(() => { if (isOpen) setEverOpened(true); }, [isOpen]);

  // Lock body scroll while open
  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  if (!everOpened) return null;

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/40 z-[9998] transition-opacity duration-300 ${
          isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        onClick={onClose}
      />

      {/* Panel */}
      <div
        className={`fixed top-0 right-0 h-screen w-[420px] bg-white dark:bg-gray-900 shadow-2xl z-[9999] flex flex-col transition-transform duration-300 ease-in-out ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-[#F7FCF3] border-b border-gray-200 dark:border-gray-700 px-5 py-4 flex items-center justify-between shrink-0 z-10">
          <h3 className="text-base font-bold text-gray-900 dark:text-gray-100">Live Preview</h3>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-[#7B7B7B] font-semibold dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto p-5">
          <EmailMockUp
            subject={subject}
            previewText={previewText}
            senderName={senderName}
            senderEmail={senderEmail}
            htmlContent={htmlContent}
            plainText={plainText}
          />
        </div>
      </div>
    </>,
    document.body
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

export const SendEmailCard: React.FC<SendEmailCardProps> = ({
  node,
  templates = [],
  onUpdate,
  onDelete,
  onReset,
  onCloseConvoAI,
  bare,
}) => {
  const [isDraftDrawerOpen, setIsDraftDrawerOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const config = node?.config ?? {};
  const channel: "Email" | "WhatsApp" =
    (config.channel === "WhatsApp" || config.channel === "whatsapp") ? "WhatsApp" : "Email";

  // Template list narrows to the selected channel. WhatsApp additionally
  // filters wa_status='APPROVED' — pending / rejected templates can't be
  // sent, so hiding them saves a wasted-select round-trip.
  const filteredTemplates = useMemo(() => {
    const ch = channel.toLowerCase();
    return templates.filter((t) => {
      const tCh = (t.channel || "").toLowerCase();
      if (ch === "whatsapp") {
        return tCh === "whatsapp" && (t.wa_status ?? "").toUpperCase() === "APPROVED";
      }
      return tCh === "email";
    });
  }, [templates, channel]);

  const selectedTemplate: TemplateModule | null = useMemo(() => {
    const configuredName = config.templateName ?? config.template ?? config.template_name ?? "";
    const configuredId = config.templateId ?? config.template_id ?? "";

    return filteredTemplates.find((t) =>
      (configuredId && t.id === configuredId) ||
      (configuredName && t.template_name === configuredName)
    ) ?? null;
  }, [config.template, config.templateId, config.templateName, config.template_id, config.template_name, filteredTemplates]);


  useEffect(() => {
    if (!selectedTemplate) return;

    const nextConfig = {
      ...config,
      templateId: selectedTemplate.id,
      templateName: selectedTemplate.template_name,
      template: selectedTemplate.template_name,
      subject: config.subject ?? selectedTemplate.subject ?? "",
      body: config.body ?? selectedTemplate.html_content ?? selectedTemplate.content ?? "",
      html: config.html ?? selectedTemplate.html_content ?? "",
      plainText: config.plainText ?? selectedTemplate.content ?? "",
      language: config.language ?? selectedTemplate.language ?? "en_US",
      channel: config.channel ?? selectedTemplate.channel ?? channel,
    };

    const needsBackfill =
      config.templateId !== nextConfig.templateId ||
      config.templateName !== nextConfig.templateName ||
      config.template !== nextConfig.template ||
      config.subject !== nextConfig.subject ||
      config.language !== nextConfig.language ||
      config.channel !== nextConfig.channel;

    if (needsBackfill) onUpdate(nextConfig);
  }, [selectedTemplate]);

  const handleTemplateChange = (templateId: string) => {
    const tpl = filteredTemplates.find((t) => t.id === templateId) ?? null;
    onUpdate({
      ...config,
      templateId: tpl?.id ?? "",
      templateName: tpl?.template_name ?? "",
      template: tpl?.template_name ?? "",
      subject: tpl?.subject ?? "",
      body: tpl?.html_content ?? tpl?.content ?? "",
      html: tpl?.html_content ?? "",
      plainText: tpl?.content ?? "",
      language: tpl?.language ?? "en_US",
      channel: tpl?.channel ?? channel,
    });
  };

  const openDraftDrawer = () => {
    onCloseConvoAI?.();
    setIsPreviewOpen(false);
    setIsDraftDrawerOpen(true);
  };

  const openPreview = () => {
    onCloseConvoAI?.();
    setIsDraftDrawerOpen(false);
    setIsPreviewOpen(true);
  };

  const handleDraftSave = (data: {
    html: string;
    subject: string;
    previewText: string;
    plainText: string;
  }) => {
    onUpdate({
      ...config,
      draftHtml: data.html,
      draftSubject: data.subject,
      draftPreviewText: data.previewText,
      draftPlainText: data.plainText,
    });
  };

  const previewHtml    = config.draftHtml       ?? selectedTemplate?.html_content ?? "";
  const previewPlain   = config.draftPlainText   ?? selectedTemplate?.content      ?? "";
  const previewSubject = config.draftSubject     ?? selectedTemplate?.subject      ?? "";
  const previewHeader  = config.draftPreviewText ?? "";

  return (
    <>
      <CardShell
        title="Select Channel"
        description="Pick a channel for this step"
        onDelete={onDelete}
        onReset={onReset}
        bare={bare}
      >
        {/* Select Channel — Email or WhatsApp. Switching resets the
            selected template since the two channels have disjoint pools. */}
        <div className="mb-3">
          <FieldLabel>Select Channel</FieldLabel>
          <div className="relative">
            <select
              value={channel}
              onChange={(e) => {
                onUpdate({
                  ...config,
                  channel: e.target.value,
                  templateId: "",
                  templateName: "",
                  template: "",
                  subject: "",
                  body: "",
                  html: "",
                  plainText: "",
                });
              }}
              className="w-full border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2.5 text-sm appearance-none bg-white dark:bg-gray-900 pr-8 focus:outline-none focus:ring-1 focus:ring-indigo-300"
            >
              {SEND_CHANNELS.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
            <ChevronDown size={16} className="absolute right-3 top-3 text-[#7B7B7B] font-semibold dark:text-gray-500 pointer-events-none" />
          </div>
        </div>

        {/* Template picker — Email path only. WhatsApp intentionally hides
            it here (Joseph 2026-08-03: "the template should disappear if
            I choose WhatsApp"). WhatsApp templates are picked on the
            downstream WhatsApp send node — the condition + wait_until
            steps route to the right one per branch, so a duplicate
            picker on this card was confusing operators. */}
        {channel === "Email" && (
          <div className="mb-4">
            <FieldLabel>Template for Email</FieldLabel>
            <div className="relative">
              <select
                value={selectedTemplate?.id ?? config.templateId ?? ""}
                onChange={(e) => handleTemplateChange(e.target.value)}
                className="w-full border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2.5 text-sm appearance-none bg-white dark:bg-gray-900 pr-8 focus:outline-none focus:ring-1 focus:ring-indigo-300"
              >
                <option value=""> Choose Email Template </option>
                {filteredTemplates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.template_name}
                  </option>
                ))}
              </select>
              <ChevronDown
                size={16}
                className="absolute right-3 top-3 text-[#7B7B7B] font-semibold dark:text-gray-500 pointer-events-none"
              />
            </div>
          </div>
        )}
        {channel === "WhatsApp" && (
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
            WhatsApp template is picked on the send node after the condition below.
          </p>
        )}

        {selectedTemplate && (
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden bg-gray-50 dark:bg-gray-800">
            <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-800">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Subject</p>
              <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                {config.draftSubject || selectedTemplate.subject || "No subject"}
              </p>
            </div>
            <div className="p-3 text-xs text-gray-600 dark:text-gray-300 max-h-32 overflow-y-auto whitespace-pre-line">
              {(config.draftPlainText || selectedTemplate.content
                ? (config.draftPlainText || selectedTemplate.content)
                    .replace(/<[^>]+>/g, " ")
                    .trim()
                    .slice(0, 220)
                : selectedTemplate.content?.slice(0, 220) || "No content preview") + "..."}
            </div>
            <div className="flex border-t border-gray-100 dark:border-gray-800 text-xs font-medium">
              <button
                type="button"
                onClick={openPreview}
                className="flex-1 py-2.5 text-indigo-600 hover:bg-indigo-50 border-r border-gray-100 dark:border-gray-800 transition-colors"
              >
                Preview
              </button>
              <button
                type="button"
                onClick={openDraftDrawer}
                className="flex-1 py-2.5 text-indigo-600 hover:bg-indigo-50 transition-colors"
              >
                Draft New
              </button>
            </div>
          </div>
        )}
      </CardShell>

      {/* Draft Drawer — portaled */}
      <DraftDrawer
        isOpen={isDraftDrawerOpen}
        onClose={() => setIsDraftDrawerOpen(false)}
        initialHtml={config.draftHtml ?? selectedTemplate?.html_content ?? ""}
        initialSubject={config.draftSubject ?? selectedTemplate?.subject ?? ""}
        initialPreviewText={config.draftPreviewText ?? ""}
        onSave={handleDraftSave}
      />

      {/* Preview Panel — portaled */}
      <PreviewPanel
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        subject={previewSubject}
        previewText={previewHeader}
        senderName={config.senderName || "VouchLinks"}
        senderEmail={config.senderEmail || "hello@vouchlinks.com"}
        htmlContent={previewHtml}
        plainText={previewPlain}
      />
    </>
  );
};